import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ChartFormatModel }         from '../typings/chart-format';
import { ChartFormatService }       from '../chart-format.service';
import { ChartInfoPanelModel }      from '../typings/chart-info-panel';
import { ChartPaddingModel }        from '../typings/chart-padding';
import { ChartPointModel }          from '../typings/chart-point';
import { ChartSeriesModel }         from '../typings/chart-series';
import { infoPanelPosition }        from '../chart-info-panel/chart-info-panel-position';
import { niceCeil, TICK_DIVISIONS } from '../chart-scale';

interface RenderedAreaSeries {
  areaPath: string;
  color   : string;
  linePath: string;
}

interface RenderedDot {
  color: string;
  x    : number;
  y    : number;
}

// Overlaid or stacked area series over evenly spaced points; hover reads out, click drills
@Component({
  selector   : 'wor-area-chart',
  styles: [ require('./area-chart.component.scss') ],
  template: require('./area-chart.component.html')
})
export class AreaChartComponent implements OnChanges {
  @Input() format    : ChartFormatModel        = 'money';
  @Input() isStacked : boolean                 = false;
  @Input() points    : Array<ChartPointModel>  = [];
  @Input() series    : Array<ChartSeriesModel> = [];
  @Input() width     : number                  = 640;

  @Output() pointPressed : EventEmitter<ChartPointModel> = new EventEmitter<ChartPointModel>();

  public dots       : Array<RenderedDot>                     = [];
  public height     : number                                 = 260;
  public hiddenKeys : Array<string>                          = [];
  public hoverDots  : Array<RenderedDot>                     = [];
  public hoverIndex : number                                 = -1;
  public hoverX     : number                                 = -1;
  public infoPanel  : ChartInfoPanelModel                    = { isVisible: false, rows: [], title: '', x: 0, y: 0 };
  public padding    : ChartPaddingModel                      = { bottom: 26, left: 54, right: 14, top: 12 };
  public rendered   : Array<RenderedAreaSeries>              = [];
  public ticks      : Array<{ label : string; y : number; }> = [];
  public xLabels    : Array<{ text : string; x : number; }>  = [];

  private yMax : number = 1;

  constructor (
    private chartFormatService: ChartFormatService
  ) {}

  ngOnChanges () : void {
    // a series that is no longer supplied cannot stay hidden
    this.hiddenKeys = this.hiddenKeys.filter(key => this.series.some(item => item.key === key));

    this.render();
  }

  formatValue ( value : number ) : string {
    return this.chartFormatService.value(value, this.format);
  }

  hideInfoPanel () : void {
    this.hoverDots = [];
    this.hoverIndex = -1;
    this.hoverX = -1;
    this.infoPanel = { ...this.infoPanel, isVisible: false };
  }

  isDrillable () : boolean {
    return this.pointPressed.observed;
  }

  isHidden ( key : string ) : boolean {
    return this.hiddenKeys.indexOf(key) > -1;
  }

  onMove ( event : MouseEvent ) : void {
    if ( !this.points.length ) {
      return;
    }

    const svg = event.currentTarget as SVGSVGElement;
    const bounds = svg.getBoundingClientRect();
    const plotX = ( event.clientX - bounds.left ) * ( this.width / bounds.width );

    this.hoverIndex = this.nearestIndex(plotX);
    this.hoverX = this.pointX(this.hoverIndex);

    const point = this.points[ this.hoverIndex ];
    const stacks = this.stackedValues(this.hoverIndex);

    this.hoverDots = this.visibleSeries().map(( item, index ) => ({
      color: item.color,
      x    : this.hoverX,
      y    : this.pointY(
        this.isStacked
          ? stacks[ index ]
          : ( point.values[ item.key ] || 0 )
      )
    }));

    this.infoPanel = {
      isVisible: true,
      rows     : this.visibleSeries().map(item => ({
        color: item.color,
        label: item.label,
        value: this.formatValue(point.values[ item.key ] || 0)
      })),
      title: point.label,
      ...infoPanelPosition(event, bounds)
    };
  }

  press () : void {
    if ( this.isDrillable() && this.hoverIndex >= 0 ) {
      this.pointPressed.emit(this.points[ this.hoverIndex ]);
    }
  }

  toggleSeries ( key : string ) : void {
    if ( !this.isHidden(key) && this.visibleSeries().length < 2 ) {
      return;
    }

    this.hiddenKeys = this.isHidden(key)
      ? this.hiddenKeys.filter(item => item !== key)
      : this.hiddenKeys.concat(key);

    this.render();
  }

  trackByIndex ( index : number ) : number {
    return index;
  }

  visibleSeries () : Array<ChartSeriesModel> {
    return this.series.filter(item => !this.isHidden(item.key));
  }

  private nearestIndex ( plotX : number ) : number {
    const count = this.points.length;

    if ( count < 2 ) {
      return 0;
    }

    const step = ( this.width - this.padding.left - this.padding.right ) / ( count - 1 );
    const index = Math.round(( plotX - this.padding.left ) / step);

    return Math.min(Math.max(index, 0), count - 1);
  }

  private pointX ( index : number ) : number {
    const innerWidth = this.width - this.padding.left - this.padding.right;
    const count = this.points.length;

    return count < 2
      ? this.padding.left + innerWidth / 2
      : this.padding.left + ( index * innerWidth ) / ( count - 1 );
  }

  private pointY ( value : number ) : number {
    const innerHeight = this.height - this.padding.top - this.padding.bottom;

    return this.padding.top + innerHeight - ( Math.max(value, 0) / this.yMax ) * innerHeight;
  }

  private render () : void {
    this.dots = [];
    this.hideInfoPanel();
    this.rendered = [];
    this.ticks = [];
    this.xLabels = [];

    if ( !this.points.length || !this.visibleSeries().length ) {
      return;
    }

    let max = 0;

    this.points.forEach(( point, index ) => {
      if ( this.isStacked ) {
        const stacks = this.stackedValues(index);

        max = Math.max(max, stacks[ stacks.length - 1 ] || 0);
      } else {
        this.visibleSeries().forEach(item => max = Math.max(max, point.values[ item.key ] || 0));
      }
    });

    this.yMax = niceCeil(max);

    const baseY = this.pointY(0);

    this.visibleSeries().forEach(( item, seriesIndex ) => {
      const upper = this.points.map(( point, index ) => {
        const value = this.isStacked
          ? this.stackedValues(index)[ seriesIndex ]
          : ( point.values[ item.key ] || 0 );

        return { x: this.pointX(index), y: this.pointY(value) };
      });

      const lower = this.points.map(( point, index ) => {
        const value = this.isStacked && seriesIndex > 0
          ? this.stackedValues(index)[ seriesIndex - 1 ]
          : 0;

        return { x: this.pointX(index), y: value > 0 ? this.pointY(value) : baseY };
      });

      const linePath = upper
        .map(( coordinate, index ) => `${ index === 0 ? 'M' : 'L' }${ coordinate.x } ${ coordinate.y }`)
        .join(' ');

      const areaPath = `${ linePath } ${ lower
        .slice()
        .reverse()
        .map(coordinate => `L${ coordinate.x } ${ coordinate.y }`)
        .join(' ') } Z`;

      this.rendered.push({ areaPath, color: item.color, linePath });
    });

    if ( this.points.length === 1 ) {
      const stacks = this.stackedValues(0);

      this.dots = this.visibleSeries().map(( item, seriesIndex ) => ({
        color: item.color,
        x    : this.pointX(0),
        y    : this.pointY(
          this.isStacked
            ? stacks[ seriesIndex ]
            : ( this.points[ 0 ].values[ item.key ] || 0 )
        )
      }));
    }

    for ( let index = 0; index <= TICK_DIVISIONS; index++ ) {
      const value = ( this.yMax / TICK_DIVISIONS ) * index;

      this.ticks.push({
        label: this.chartFormatService.axis(value, this.format),
        y    : this.pointY(value)
      });
    }

    const labelIndexes = this.points.length < 3
      ? this.points.map(( _point, index ) => index)
      : [ 0, Math.floor(( this.points.length - 1 ) / 2), this.points.length - 1 ];

    labelIndexes.forEach(index => {
      this.xLabels.push({
        text: this.points[ index ].label,
        x   : this.pointX(index)
      });
    });
  }

  private stackedValues ( index : number ) : Array<number> {
    const point = this.points[ index ];
    let running = 0;

    return this.visibleSeries().map(item => {
      running += point.values[ item.key ] || 0;

      return running;
    });
  }
}
